import React, { useMemo, useRef, useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import { Arbeitsschritt } from './types';
import { useGefaehrdungsVorschlag } from './hooks/useGefaehrdungsVorschlag';
import { Button } from '../Button';
import { AlertTriangle, Bot, CheckSquare, Loader2, ShieldCheck, Square, StopCircle } from 'lucide-react';

// Schritt 4 des Anlage-Assistenten: KI-Vorschläge für Schutzmaßnahmen und
// Vorschriften über alle Schritte ohne Maßnahmen. Sequentiell mit kleiner
// Pause (Rate-Limits), abbrechbar; ein Fehlschlag stoppt den Lauf nicht.
// Geschrieben wird erst nach der Durchsicht — in arbeitsschritte.schutzmassnahmen[]
// bzw. .vorschriften[], also den Feldern, aus denen das PDF liest.

interface Props {
  schritte: Arbeitsschritt[];
  onUebernommen: () => void | Promise<void>;
  melde: (text: string, ton?: 'info' | 'fehler') => void;
}

interface SchrittErgebnis {
  massnahmen: string[];
  vorschriften: string[];
  fehler?: string;
}

const PAUSE_MS = 400;

const eindeutig = (werte: string[]) => Array.from(new Set(werte.map(w => w.trim()).filter(Boolean)));

/** KI-Antwort → flache Listen für Maßnahmen und Vorschriften. */
const leseVorschlag = (daten: any): { massnahmen: string[]; vorschriften: string[] } => {
  const massnahmen: string[] = [];
  const vorschriften: string[] = [];
  (daten?.gefaehrdungen || []).forEach((g: any) => {
    (g?.schutzmassnahmen || []).forEach((m: any) => {
      if (m?.beschreibung) massnahmen.push(String(m.beschreibung));
      if (m?.regelwerkReferenz) vorschriften.push(String(m.regelwerkReferenz));
    });
  });
  return { massnahmen: eindeutig(massnahmen), vorschriften: eindeutig(vorschriften) };
};

export const WizardStepGefaehrdungen: React.FC<Props> = ({ schritte, onUebernommen, melde }) => {
  const { suggestHazards } = useGefaehrdungsVorschlag();
  const [ergebnisse, setErgebnisse] = useState<Record<string, SchrittErgebnis>>({});
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [fortschritt, setFortschritt] = useState<{ aktuell: number; gesamt: number } | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const abbruchRef = useRef(false);

  const offeneSchritte = useMemo(
    () => schritte.filter(s => !(s.schutzmassnahmen || []).length),
    [schritte],
  );

  const schluessel = (id: string, art: 'm' | 'v', text: string) => `${id}|${art}|${text}`;

  const starten = async () => {
    if (offeneSchritte.length === 0) return;
    abbruchRef.current = false;
    setLaeuft(true);
    const gesammelt: Record<string, SchrittErgebnis> = {};
    const vorausgewaehlt = new Set<string>();
    try {
      for (let i = 0; i < offeneSchritte.length; i++) {
        if (abbruchRef.current) break;
        const schritt = offeneSchritte[i];
        setFortschritt({ aktuell: i + 1, gesamt: offeneSchritte.length });
        const antwort = await suggestHazards(schritt.id);
        if (antwort.status === 'fehler') {
          gesammelt[schritt.id] = {
            massnahmen: [], vorschriften: [],
            fehler: antwort.art === 'netzwerk'
              ? `KI-Dienst nicht erreichbar (${antwort.fehler})`
              : `Antwort nicht auswertbar (${antwort.fehler})`,
          };
        } else {
          const { massnahmen, vorschriften } = leseVorschlag(antwort.data);
          gesammelt[schritt.id] = { massnahmen, vorschriften };
          massnahmen.forEach(t => vorausgewaehlt.add(schluessel(schritt.id, 'm', t)));
          vorschriften.forEach(t => vorausgewaehlt.add(schluessel(schritt.id, 'v', t)));
        }
        // Zwischenstand sofort zeigen — der Lauf dauert je Schritt mehrere Sekunden.
        setErgebnisse({ ...gesammelt });
        setAuswahl(new Set(vorausgewaehlt));
        if (i < offeneSchritte.length - 1 && !abbruchRef.current) {
          await new Promise(r => setTimeout(r, PAUSE_MS));
        }
      }
    } finally {
      setLaeuft(false);
      setFortschritt(null);
    }
  };

  const umschalten = (key: string) =>
    setAuswahl(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const uebernehmen = async () => {
    setSpeichert(true);
    try {
      let geschrieben = 0;
      for (const schritt of offeneSchritte) {
        const ergebnis = ergebnisse[schritt.id];
        if (!ergebnis) continue;
        const neueM = ergebnis.massnahmen.filter(t => auswahl.has(schluessel(schritt.id, 'm', t)));
        const neueV = ergebnis.vorschriften.filter(t => auswahl.has(schluessel(schritt.id, 'v', t)));
        if (neueM.length === 0 && neueV.length === 0) continue;
        const { error } = await supabase.from('sige_arbeitsschritte').update({
          schutzmassnahmen: eindeutig([...(schritt.schutzmassnahmen || []), ...neueM]),
          vorschriften: eindeutig([...(schritt.vorschriften || []), ...neueV]),
        }).eq('id', schritt.id);
        if (error) throw error;
        geschrieben++;
      }
      if (geschrieben === 0) {
        melde('Nichts ausgewählt — es wurde nichts geschrieben.', 'fehler');
        return;
      }
      setErgebnisse({});
      setAuswahl(new Set());
      melde(`Schutzmaßnahmen und Vorschriften für ${geschrieben} Arbeitsschritt(e) übernommen.`);
      await onUebernommen();
    } catch (e: any) {
      console.error('Übernahme der KI-Maßnahmen fehlgeschlagen', e);
      melde(`Übernahme fehlgeschlagen: ${e?.message || String(e)}`, 'fehler');
    } finally {
      setSpeichert(false);
    }
  };

  const anzahlGewaehlt = auswahl.size;
  const hatErgebnisse = Object.keys(ergebnisse).length > 0;

  if (schritte.length === 0) {
    return (
      <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-4">
        Der Plan enthält noch keine Arbeitsschritte. Bitte zuerst Schritt 2 oder 3 durchlaufen.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Für alle Arbeitsschritte <strong>ohne Schutzmaßnahme</strong> werden Vorschläge nach RAB 30
        eingeholt (DGUV, ASR, BaustellV). Jeder Vorschlag ist einzeln abwählbar; geschrieben wird
        erst mit „Übernehmen".
      </p>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[12rem]">
          <div className="text-sm font-semibold text-slate-800">
            {offeneSchritte.length} von {schritte.length} Arbeitsschritten ohne Schutzmaßnahme
          </div>
          {fortschritt && (
            <div className="mt-2">
              <div className="text-xs text-slate-500 mb-1">
                Arbeitsschritt {fortschritt.aktuell} von {fortschritt.gesamt} …
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 transition-all" style={{ width: `${(fortschritt.aktuell / fortschritt.gesamt) * 100}%` }} />
              </div>
            </div>
          )}
        </div>
        {laeuft ? (
          <Button variant="secondary" onClick={() => { abbruchRef.current = true; }} className="text-sm">
            <StopCircle className="w-4 h-4 mr-2" /> Abbrechen
          </Button>
        ) : (
          <Button data-tutorial="sigeplan.gefaehrdungen-ki" variant="ai" onClick={starten} disabled={offeneSchritte.length === 0} className="text-sm">
            <Bot className="w-4 h-4 mr-2" />
            {hatErgebnisse ? 'Erneut durchlaufen' : `KI-Lauf starten (${offeneSchritte.length})`}
          </Button>
        )}
      </div>

      {hatErgebnisse && (
        <div className="space-y-3">
          {offeneSchritte.filter(s => ergebnisse[s.id]).map(schritt => {
            const ergebnis = ergebnisse[schritt.id];
            return (
              <div key={schritt.id} className="border border-slate-200 rounded-xl bg-white p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{schritt.bezeichnung}</div>
                    <div className="text-[11px] text-slate-400 truncate">{schritt.hauptabschnitt || 'Ohne Abschnitt'}</div>
                  </div>
                </div>

                {ergebnis.fehler ? (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{ergebnis.fehler}</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                        <ShieldCheck className="w-3.5 h-3.5 text-green-600" /> Schutzmaßnahmen
                      </div>
                      {ergebnis.massnahmen.length === 0 && <div className="text-xs text-slate-400 italic">Kein Vorschlag.</div>}
                      {ergebnis.massnahmen.map(t => {
                        const key = schluessel(schritt.id, 'm', t);
                        return (
                          <button key={key} onClick={() => umschalten(key)} className="w-full flex items-start gap-2 text-left py-0.5">
                            {auswahl.has(key) ? <CheckSquare className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" /> : <Square className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />}
                            <span className="text-xs text-slate-700">{t}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div>
                      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Vorschriften</div>
                      {ergebnis.vorschriften.length === 0 && <div className="text-xs text-slate-400 italic">Kein Vorschlag.</div>}
                      {ergebnis.vorschriften.map(t => {
                        const key = schluessel(schritt.id, 'v', t);
                        return (
                          <button key={key} onClick={() => umschalten(key)} className="w-full flex items-start gap-2 text-left py-0.5">
                            {auswahl.has(key) ? <CheckSquare className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" /> : <Square className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />}
                            <span className="text-xs text-slate-700">{t}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="flex justify-end sticky bottom-0 bg-white/90 backdrop-blur-sm py-2">
            <Button variant="primary" onClick={uebernehmen} disabled={speichert || laeuft || anzahlGewaehlt === 0}>
              {speichert && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Übernehmen ({anzahlGewaehlt} Einträge)
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
